import * as React from 'react';
import { useId } from 'react';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
  InputGroup,
  InputGroupAddon,
  InputGroupTextarea,
  Textarea
} from '@bliks-io/ui';

export interface FormFieldTextareaProps extends Omit<React.ComponentProps<'textarea'>, 'prefix'> {
  /**
   * Field label
   */
  label?: string;

  /**
   * Field description/helper text
   */
  description?: string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Mark field as required
   */
  required?: boolean;

  /**
   * Use InputGroup for enhanced functionality
   */
  useInputGroup?: boolean;

  /**
   * Prefix content (label, counter, etc.) at the top - requires useInputGroup=true
   */
  prefix?: React.ReactNode;

  /**
   * Suffix content (character count, etc.) at the bottom - requires useInputGroup=true
   */
  suffix?: React.ReactNode;

  /**
   * Field orientation
   */
  orientation?: 'vertical' | 'horizontal' | 'responsive';

  /**
   * Additional class for Field wrapper
   */
  fieldClassName?: string;

  /**
   * HTML autocomplete attribute.
   * Defaults to "off" to prevent browser autofill on generic fields.
   * Set explicitly to enable autofill (e.g., "street-address").
   */
  autoComplete?: string;
}

/**
 * Reusable form field component combining Field + Textarea/InputGroup
 * Can be used standalone or wrapped by RHF adapter components
 */
export const FormFieldTextarea = React.forwardRef<HTMLTextAreaElement, FormFieldTextareaProps>(
  (
    {
      label,
      description,
      error,
      required = false,
      useInputGroup = false,
      prefix,
      suffix,
      orientation = 'vertical',
      fieldClassName,
      className,
      id,
      autoComplete,
      'aria-invalid': ariaInvalid,
      ...textareaProps
    },
    ref
  ) => {
    // Generate unique prefix to prevent browser autofill heuristics
    const uniqueId = useId();
    const fieldPrefix = `bliks-field${uniqueId}`;

    // Extract and transform name to prevent autofill pattern matching
    const { name: originalName, ...restTextareaProps } = textareaProps;
    const htmlName = originalName ? `${fieldPrefix}-${originalName}` : undefined;
    const fieldId = id || htmlName || fieldPrefix;

    const hasError = !!error;
    const invalid = ariaInvalid ?? hasError;

    // Common textarea props with autofill prevention
    const commonTextareaProps = {
      ref,
      id: fieldId,
      name: htmlName,
      autoComplete: autoComplete ?? 'off',
      'aria-invalid': invalid,
      'data-field': originalName, // Original field name for debugging
      className,
      ...restTextareaProps
    };

    // Render content based on whether InputGroup is needed
    const renderTextarea = () => {
      if (useInputGroup || prefix || suffix) {
        return (
          <InputGroup>
            {prefix && <InputGroupAddon align="block-start">{prefix}</InputGroupAddon>}
            <InputGroupTextarea {...commonTextareaProps} />
            {suffix && <InputGroupAddon align="block-end">{suffix}</InputGroupAddon>}
          </InputGroup>
        );
      }

      return <Textarea {...commonTextareaProps} />;
    };

    return (
      <Field orientation={orientation} className={fieldClassName} data-invalid={hasError}>
        {label && (
          <FieldLabel htmlFor={fieldId}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </FieldLabel>
        )}
        <FieldContent>
          {description && <FieldDescription>{description}</FieldDescription>}
          {renderTextarea()}
          {error && <FieldError>{error}</FieldError>}
        </FieldContent>
      </Field>
    );
  }
);

FormFieldTextarea.displayName = 'FormFieldTextarea';
